<template>
  <div
    class="v2-product-detail-content"
    :class="{
      'v2-product-detail-content--page': presentation === 'page',
      'v2-product-detail-content--modal': presentation === 'modal',
    }"
  >
    <!-- Loading skeleton — mirrors the product layout (gallery + info) using the
         same wrappers, so the popup opens at its full size immediately (no
         small→big jump) and shimmers until the data arrives. -->
    <div
      v-if="loading"
      class="v2-product-detail-content__skeleton"
      aria-hidden="true"
    >
      <v2-card no-padding>
        <div class="v2-product-detail-content__main">
          <div class="v2-product-detail-content__gallery">
            <div class="v2-pd-sk v2-pd-sk--image" />
          </div>
          <div class="v2-product-detail-content__info">
            <div class="v2-pd-sk v2-pd-sk--title" />
            <div class="v2-pd-sk v2-pd-sk--sub" />
            <div class="v2-pd-sk v2-pd-sk--price" />
            <div class="v2-pd-sk-divider" />
            <div
              v-for="n in 6"
              :key="`pd-sk-${n}`"
              class="v2-pd-sk v2-pd-sk--line"
              :class="{ 'v2-pd-sk--line-short': n % 3 === 0 }"
            />
            <div class="v2-pd-sk v2-pd-sk--btn" />
          </div>
        </div>
      </v2-card>
    </div>

    <template v-else>
      <!-- Product not found -->
      <div
        v-if="product === undefined"
        class="v2-product-detail-content__not-found"
      >
        <v2-card>
          <div class="v2-text-center v2-p-8">
            <h2 class="v2-heading-sm v2-mb-2">
              Product not found
            </h2>
            <p class="v2-text-muted">
              The product you are looking for is not available.
            </p>
          </div>
        </v2-card>
      </div>

      <!-- Product content -->
      <template v-if="product">
        <!-- Page-only chrome: back button + page title (modal presentation supplies its own chrome) -->
        <div
          v-if="presentation === 'page'"
          class="v2-product-detail-content__header-row"
        >
          <button
            class="v2-product-detail-content__back-btn"
            @click="goBack"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line
                x1="19"
                y1="12"
                x2="5"
                y2="12"
              />
              <polyline points="12 19 5 12 12 5" />
            </svg>
          </button>
          <h2 class="v2-product-detail-content__page-title">
            Product Details
          </h2>
        </div>

        <v2-card
          no-padding
          :class="{ 'v2-product-detail-content--price-na': isPriceNA }"
        >
          <div class="v2-product-detail-content__main">
            <!-- Left: Gallery -->
            <div class="v2-product-detail-content__gallery">
              <v2-product-gallery
                :images="parseProductImages(product)"
                :product-name="product.name"
              />
            </div>

            <!-- Right: Info -->
            <div class="v2-product-detail-content__info">
              <!-- Product Name + Actions -->
              <div class="v2-product-detail-content__header">
                <h1 class="v2-product-detail-content__name">
                  {{ product.name }}
                </h1>
                <div
                  v-if="isBrand"
                  class="v2-product-detail-content__actions-wrap"
                >
                  <button
                    class="v2-product-detail-content__more-btn"
                    data-test="kebab-btn"
                    @click="showActionsBox = !showActionsBox"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <circle
                        cx="12"
                        cy="5"
                        r="1"
                      />
                      <circle
                        cx="12"
                        cy="12"
                        r="1"
                      />
                      <circle
                        cx="12"
                        cy="19"
                        r="1"
                      />
                    </svg>
                  </button>
                  <div
                    v-show="showActionsBox"
                    class="v2-product-detail-content__actions-menu"
                  >
                    <div class="v2-product-detail-content__actions-title">
                      Product's Actions
                    </div>
                    <button
                      class="v2-product-detail-content__action-item"
                      data-test="kebab-delete"
                      @click="showDeleteModal"
                    >
                      Delete Product
                    </button>
                    <button
                      class="v2-product-detail-content__action-item"
                      data-test="kebab-copy-link"
                      @click="handleCopyLink"
                    >
                      Copy Product's Link
                    </button>
                    <button
                      v-if="isEditEnabled"
                      class="v2-product-detail-content__action-item"
                      data-test="kebab-edit"
                      @click="onEditClick"
                    >
                      Edit Product
                    </button>
                  </div>
                </div>
              </div>

              <!-- Breadcrumb -->
              <div
                v-if="product.department || product.category || product.subCategory"
                class="v2-product-detail-content__breadcrumb"
              >
                <span v-if="product.department">{{ product.department }}</span>
                <span v-if="product.category"> / {{ product.category }}</span>
                <span v-if="product.subCategory"> / {{ product.subCategory }}</span>
              </div>

              <!-- Style Code -->
              <div class="v2-product-detail-content__style-code">
                Style Code: {{ product.styleCode }}
              </div>

              <!-- KP-4350: tags from the retailer's segment (e.g. MCA) -->
              <v2-segment-tags
                :product="product"
                inline
                class="v2-product-detail-content__segment-tags"
              />

              <!-- Pricing -->
              <div class="v2-product-detail-content__prices">
                <div class="v2-product-detail-content__price-item">
                  <span class="v2-product-detail-content__price-label">Retail:</span>
                  <span>{{ formatCurrency(product.retailPrice, product.currency) }}</span>
                </div>
                <div
                  v-if="!isGuest && isShowWsp"
                  class="v2-product-detail-content__price-item"
                >
                  <span class="v2-product-detail-content__price-label">Wholesale:</span>
                  <span
                    v-if="!product.discountedPrice"
                    :class="{ 'v2-product-detail-content__line-through': product.discountedPrice }"
                  >
                    {{ formatCurrency(product.price, product.currency) }}
                  </span>
                  <span
                    v-if="product.discountedPrice"
                    class="v2-product-detail-content__discounted"
                  >
                    {{ formatCurrency(product.discountedPrice, product.currency) }}
                  </span>
                </div>
              </div>

              <div class="v2-product-detail-content__divider" />

              <!-- Details -->
              <div class="v2-product-detail-content__meta">
                <div class="v2-product-detail-content__meta-item">
                  <span class="v2-product-detail-content__meta-label">Description:</span>
                  <span v-html="replaceURLTextWithHyperLink(product.descriptionLong)" />
                </div>
                <div class="v2-product-detail-content__meta-item">
                  <span class="v2-product-detail-content__meta-label">Material:</span>
                  <span>{{ product.descriptionMaterials }}</span>
                </div>
                <div class="v2-product-detail-content__meta-item">
                  <span class="v2-product-detail-content__meta-label">Country of Origin:</span>
                  <span>{{ product.country }}</span>
                </div>
                <div class="v2-product-detail-content__meta-item">
                  <span class="v2-product-detail-content__meta-label">Gender:</span>
                  <span>{{ product.gender }}</span>
                </div>
                <div
                  v-if="product.shippingStartDate && product.shippingEndDate"
                  class="v2-product-detail-content__meta-item"
                >
                  <span class="v2-product-detail-content__meta-label">Availability Date:</span>
                  <span>{{ formattedDate(product.shippingStartDate) }} to {{ formattedDate(product.shippingEndDate) }}</span>
                </div>
              </div>

              <div class="v2-product-detail-content__divider" />

              <!-- Carton Quantity -->
              <div
                v-if="product.isCarton && !isGuest"
                class="v2-product-detail-content__carton-qty"
              >
                <label class="v2-product-detail-content__meta-label">Quantity:</label>
                <input
                  v-model.number="quantity"
                  type="number"
                  class="v2-product-detail-content__qty-input"
                  min="1"
                  :max="getMaxQuantity(product.quantityAvailable)"
                  :disabled="isPriceNA || isBrand || inactiveCollection || isBrandNotAccessible"
                >
                <span
                  v-if="hasProductMaxQuantity(product.quantityAvailable)"
                  class="v2-product-detail-content__max-qty"
                >
                  Max Qty: {{ product.quantityAvailable }}
                </span>
              </div>

              <!-- Variants -->
              <v2-product-variants
                :items="product.items"
                :is-edit-enabled="true"
                :is-carton="product.isCarton"
                :disabled-product="inactiveCollection || isBrand"
                :is-guest="isGuest"
                :min-qty-per-size="productMinQtyPerSize(product)"
                @update-variants="updateVariants"
              />

              <!-- MOQ -->
              <div class="v2-product-detail-content__moq">
                <span
                  v-if="productMinQtyPerStyle(product)"
                  class="v2-product-detail-content__moq-item"
                  :class="{ 'v2-product-detail-content__moq-item--error': isNotValidPerStyle(product, lineTotalQuantity) }"
                >
                  MOQ per style: {{ productMinQtyPerStyle(product) }}
                </span>
                <span
                  v-if="productMinQtyPerSize(product)"
                  class="v2-product-detail-content__moq-item"
                  :class="{ 'v2-product-detail-content__moq-item--error': isNotValidPerSize(product, lineTotalQuantity) }"
                >
                  MOQ per size: {{ productMinQtyPerSize(product) }}
                </span>
                <span
                  v-if="segmentMaxQtyLabel(product)"
                  class="v2-product-detail-content__moq-item"
                  :class="{ 'v2-product-detail-content__moq-item--error': isSegmentQuantityBlocked(product, lineTotalQuantity) }"
                >
                  {{ segmentMaxQtyLabel(product) }}
                </span>
              </div>

              <!-- Cart + Wishlist buttons -->
              <template v-if="isShowWsp">
                <div class="v2-product-detail-content__divider" />
                <div class="v2-product-detail-content__cta">
                  <div v-if="isRetailer">
                    <v2-button
                      :disabled="isPriceNA || inactiveCollection || isBrandNotAccessible || isNotValidMinQty(product, lineTotalQuantity) || (!product.isInCart && isSegmentQuantityBlocked(product, lineTotalQuantity))"
                      @click="!isPriceNA && reactHandleCartActionClick(product)"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <circle
                          cx="9"
                          cy="21"
                          r="1"
                        />
                        <circle
                          cx="20"
                          cy="21"
                          r="1"
                        />
                        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                      </svg>
                      {{ product.isInCart ? 'View In Cart' : 'Add to Cart' }}
                    </v2-button>
                  </div>
                  <v2-product-wishlists-dropdown
                    :product="product"
                    :wishlists="wishlists"
                    :disabled="inactiveCollection || isBrandNotAccessible"
                    :is-selections="isBrand"
                    :product-wishlists-count="product.wishlists ? product.wishlists.length : 0"
                    @select-wishlist-on-product="onSelectWishlistOnProduct(product, $event)"
                    @toggle-wishlist-dropdown="onToggleWishlistDropdown(product, $event)"
                    @create-wishlist="openWishlistModal('create_wishlist_modal_' + product._id)"
                  />
                </div>
              </template>
            </div>
          </div>
        </v2-card>

        <!-- Recommended Products -->
        <div
          v-if="suggestedProducts.length"
          class="v2-product-detail-content__recommended"
        >
          <h2 class="v2-product-detail-content__section-title">
            Recommended for you
          </h2>
          <div class="v2-product-detail-content__grid">
            <v2-related-product-card
              v-for="sp in suggestedProducts"
              :key="sp._id"
              :wishlist-id="wishlistId"
              :product="sp"
              :wishlists="wishlists"
              :inactive-collection="inactiveCollection"
              :is-wishlist-dropdown-opened="isWishlistDropdownOpened"
              @select-wishlist-on-product="onSelectWishlistOnProduct(sp, $event)"
              @toggle-wishlist-dropdown="onToggleWishlistDropdown(sp, $event)"
              @create-wishlist="openWishlistModal('create_wishlist_modal_recommended' + sp._id)"
              @open-product="onRelatedProductOpen"
            />
          </div>
        </div>

        <!-- Infinite Scroll Loading -->
        <div
          v-if="continueLoading"
          ref="loadingIcon"
          class="v2-flex v2-justify-center v2-p-6"
        >
          <v2-spinner label="Loading more..." />
        </div>

        <!-- Delete Confirmation Modal -->
        <v2-modal
          :show="showDeleteConfirm"
          title="Confirmation Step"
          size="lg"
          class="v2-product-detail-content__delete-modal"
          @close="showDeleteConfirm = false"
        >
          <p
            class="v2-text-center v2-mt-4"
            v-html="productRemoveMessage"
          />
          <template #footer>
            <div class="v2-flex v2-justify-center v2-gap-3">
              <v2-button
                v-if="canDeleteProduct"
                variant="outline"
                @click="showDeleteConfirm = false"
              >
                Cancel
              </v2-button>
              <v2-button
                v-if="canDeleteProduct"
                variant="destructive"
                :loading="isSaving"
                @click="confirmDeleteProduct"
              >
                {{ isSaving ? 'Removing Product...' : 'Yes, remove the product' }}
              </v2-button>
              <v2-button
                v-if="!canDeleteProduct"
                @click="showDeleteConfirm = false"
              >
                Ok! I understand
              </v2-button>
            </div>
          </template>
        </v2-modal>

        <!-- Wishlist List Modal -->
        <v2-wishlist-list-modal
          v-if="showWishlistModal"
          :show="showWishlistModal"
          :is-disable-create-wishlist="false"
          @close="showWishlistModal = false"
          @move-to-wishlist="reactToggleProductInWishlist({ ...$event, product })"
        />

        <!-- Product Edit Modal -->
        <v2-product-edit-modal
          v-if="showEditModal"
          :visible="showEditModal"
          :product-id="product && product._id"
          :collection-id="collectionId"
          :wishlist-id="wishlistId"
          @close="showEditModal = false"
          @edit-success="onEditSuccess"
        />
      </template>
    </template>
  </div>
</template>

<script>
import { watch } from '@vue/composition-api'
import { useProductDetails } from '@/composables/useProductDetails'
import { replaceURLTextWithHyperLink, convertToHtml } from '@/common-utils'

import constants, { FLAG_CONSTANTS } from '@/constants'
import store from '@/store'
import analytics from '@/@core/utils/analytics'
import {
  getMaxQuantity,
  hasProductMaxQuantity,
  formatObject,
} from '@core/utils/utils'
import {
  isNotValidMinQty,
  isNotValidPerStyle,
  isNotValidPerSize,
  isSegmentQuantityBlocked,
  productMinQtyPerSize,
  productMinQtyPerStyle,
  segmentMaxQtyLabel,
} from '@/views/apps/checkout/cart-products-utils'
import { SET_IS_ENABLE_CREATE_WISHLIST } from '@/store/modules/wishlist.module'
import V2Card from '@/components/v2/V2Card.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import V2ProductWishlistsDropdown from '@/components/v2/V2ProductWishlistsDropdown.vue'
import V2WishlistListModal from '@/views/v2/collection/V2WishlistListModal.vue'
import UserRoleMixin from '@/views/apps/UserRoleMixin.vue'
import V2SegmentTags from '@/components/v2/V2SegmentTags.vue'
import V2ProductGallery from './components/V2ProductGallery.vue'
import V2ProductVariants from './components/V2ProductVariants.vue'
import V2RelatedProductCard from './components/V2RelatedProductCard.vue'

export default {
  name: 'V2ProductDetailContent',
  components: {
    V2Card,
    V2Button,
    V2Modal,
    V2Spinner,
    V2ProductWishlistsDropdown,
    V2ProductGallery,
    V2ProductVariants,
    V2RelatedProductCard,
    V2SegmentTags,
    V2WishlistListModal,
    V2ProductEditModal: () => import('./edit/V2ProductEditModal.vue'),
  },
  mixins: [UserRoleMixin],
  props: {
    presentation: {
      type: String,
      default: 'modal',
      validator: v => ['page', 'modal'].includes(v),
    },
    productId: {
      type: String,
      default: '',
    },
    collectionId: {
      type: String,
      default: '',
    },
    wishlistId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      showActionsBox: false,
      showDeleteConfirm: false,
      showWishlistModal: false,
      showEditModal: false,
      isSaving: false,
      canDeleteProduct: false,
      productRemoveMessage: '',
      wishlistDropdownOpenedCount: 0,
      getMaxQuantity,
      hasProductMaxQuantity,
      replaceURLTextWithHyperLink,
      isNotValidMinQty,
      isNotValidPerStyle,
      isNotValidPerSize,
      isSegmentQuantityBlocked,
      productMinQtyPerSize,
      productMinQtyPerStyle,
      segmentMaxQtyLabel,
    }
  },
  computed: {
    isPriceNA() {
      return this.product?.price === 'N/A' || this.product?.retailPrice === 'N/A'
    },
    isShowWsp() {
      const CONNECTION_REQUEST_STATUS = constants.CONNECTION_REQUEST_STATUS || {}
      return !(
        this.isWspHidden
        && this.requestStatus !== CONNECTION_REQUEST_STATUS?.APPROVED
      )
    },
    isEditEnabled() {
      return store.state.flags[FLAG_CONSTANTS.PRODUCT_EDIT]
    },
    isWishlistDropdownOpened() {
      return !!this.wishlistDropdownOpenedCount
    },
  },
  destroyed() {
    // Page-exit analytics signal — only meaningful for the standalone route
    // page. In modal presentation the component is destroyed on every modal
    // close, which would inflate the metric.
    if (this.presentation !== 'page') return
    analytics.track(
      this.isBrand
        ? constants.TRACKS.ACTIONS.BRAND_END_VIEWING_PRODUCT
        : constants.TRACKS.ACTIONS.RETAILER_END_VIEWING_PRODUCT,
      formatObject(this.productDetails),
    )
  },
  methods: {
    goBack() {
      this.$router.back()
    },
    // KP-3382: a recommended product was clicked. In the modal flow, bubble it
    // up so V2ProductDetailModal swaps the popup content in place (it re-keys on
    // productId). On the standalone product page, navigate in-app instead.
    onRelatedProductOpen(product) {
      if (this.presentation === 'modal') {
        this.$emit('open-product', product)
        return
      }
      const params = { id: product._id, collectionId: product.collectionId }
      let name = 'v2-product-detail'
      if (this.wishlistId) {
        name = 'v2-product-detail-with-wishlist'
        params.wishlistId = this.wishlistId
      }
      this.$router.push({ name, params }).catch(() => {})
    },
    onToggleWishlistDropdown(product, isOpened = false) {
      this.wishlistDropdownOpenedCount += isOpened ? 1 : -1
      product.isOpenedDropdown = isOpened
    },
    openWishlistModal() {
      this.$store.commit(SET_IS_ENABLE_CREATE_WISHLIST, true)
      this.showWishlistModal = true
    },
    handleCopyLink() {
      this.showActionsBox = false
      this.copyProductLink()
    },
    async showDeleteModal() {
      this.showActionsBox = false
      this.isSaving = true
      try {
        await this.removeProduct(false)
        // If removeProduct succeeds without error, product was deleted
        this.isSaving = false
        this.afterSuccessfulDelete()
      } catch (err) {
        const { response } = err
        const { data } = response || {}
        const canDelete = data?.data
        const { message } = data || {}
        this.productRemoveMessage = convertToHtml(message)
        this.canDeleteProduct = !!canDelete?.shouldProceed
        this.showDeleteConfirm = true
        this.isSaving = false
      }
    },
    async confirmDeleteProduct() {
      this.isSaving = true
      try {
        const result = await this.removeProduct(true)
        if (result?.success) {
          this.showDeleteConfirm = false
          this.showActionsBox = false
          this.afterSuccessfulDelete()
        }
      } catch {
        // Error already handled by composable
      }
      this.isSaving = false
    },
    afterSuccessfulDelete() {
      if (this.presentation === 'modal') {
        this.$emit('deleted', { productId: this.productId })
      } else {
        this.navigateToCollection()
      }
    },
    navigateToCollection() {
      const collectionId = this.$route.params.collectionId
      if (collectionId) {
        this.$router.push({
          name: 'v2-collection/products',
          params: { collectionId },
        })
      }
    },
    onEditClick() {
      if (this.presentation === 'modal') {
        this.showActionsBox = false
        this.$emit('request-edit')
      } else {
        this.editProduct()
      }
    },
    async editProduct() {
      this.showActionsBox = false
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_CLICKS_EDIT_PRODUCT_OPTION, {
        product: this.product,
      })
      try {
        await this.fetchProduct(true)
        this.showEditModal = true
        analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_VIEWS_PRODUCT_EDIT_MODAL, {
          product: this.product,
        })
      } catch {
        // handled by composable
      }
    },
    onEditSuccess() {
      this.showEditModal = false
      this.loadProductDetails()
    },
  },
  setup(props, { root }) {
    const routeShim = {
      params: {
        id: props.productId,
        collectionId: props.collectionId,
        wishlistId: props.wishlistId,
      },
      // Preserve real query params (retailerId etc.) from the actual route.
      query: root.$route.query,
    }
    const composable = useProductDetails(routeShim)
    // KP-3382: clicking a recommended product router.pushes to the same
    // `v2-product-detail` route, so Vue reuses this component and only the
    // reactive `productId` prop changes — but `routeShim` was built once. Sync
    // the shim and reload the product + recommendations when the id changes.
    watch(
      () => props.productId,
      (newId, oldId) => {
        if (!newId || newId === oldId) return
        routeShim.params.id = newId
        routeShim.params.collectionId = props.collectionId
        routeShim.params.wishlistId = props.wishlistId
        composable.loadProductDetails()
      },
    )
    // The composable exposes `wishlistId` as a ref initialized from
    // `route.params.wishlistId` (i.e. our prop). Excluding it from the
    // returned bindings prevents a name collision with the `wishlistId`
    // prop. The template reads the prop directly, which carries the same
    // value the composable would have produced.
    // eslint-disable-next-line no-unused-vars
    const { wishlistId, ...rest } = composable
    return rest
  },
}
</script>

<style lang="scss" scoped>
.v2-product-detail-content {
  ::v-deep .v2-card {
    overflow: visible;
  }

  // When rendered inside V2ProductDetailModal (presentation="modal"),
  // keep the glassy treatment (backdrop-filter blur + glass-stroke
  // borders + glass-shadow) but force the SURFACE color to white. The
  // upstream glass tokens use a black-tint translucent fill which reads
  // muddy when stacked inside an opaque modal panel; a white tint keeps
  // the glass effect crisp without darkening the content.
  &--modal {
    ::v-deep .v2-card--panel,
    ::v-deep .v2-card--card {
      background: #ffffff;
    }

    // V2ProductVariants paints multiple glass surfaces (root table,
    // header row, sticky first column, body cells, quantity inputs).
    // Keep its blur/borders but flip every translucent fill to white.
    ::v-deep .v2-product-variants {
      background: #ffffff;

      thead,
      th,
      td:first-child {
        background: #ffffff !important;
      }

      tbody td {
        background: transparent;
      }

      input {
        background: #ffffff;
      }
    }
  }

  &__header-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }

  &__back-btn {
    background: none;
    border: 1px solid var(--v2-border);
    border-radius: 0.5rem;
    padding: 0.375rem;
    cursor: pointer;
    color: var(--v2-foreground);
    display: flex;
    align-items: center;

    &:hover {
      background: var(--v2-muted);
    }
  }

  &__page-title {
    font-family: var(--v2-font-display);
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--v2-foreground);
    margin: 0;
  }

  &__main {
    display: grid;
    grid-template-columns: 5fr 7fr;
    gap: var(--v2-space-8);
    padding: var(--v2-space-6);

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  // ===== Loading skeleton — reuses __main/__gallery/__info so it fills the
  // same footprint as the loaded content (popup opens at full size). =========
  &__skeleton {
    .v2-pd-sk {
      border-radius: var(--v2-radius-md);
      background-color: rgba(0, 0, 0, 0.07);
      background-image: linear-gradient(
        90deg,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 0.55) 50%,
        rgba(255, 255, 255, 0) 100%
      );
      background-size: 200% 100%;
      background-repeat: no-repeat;
      animation: v2-pd-shimmer 1.4s ease-in-out infinite;
    }

    .v2-pd-sk--image {
      // Match the real gallery's main image (V2ProductGallery __main:
      // aspect-ratio 1, min-height 300px) so the skeleton is the same height
      // as the loaded modal — no resize jump when content swaps in.
      width: 100%;
      aspect-ratio: 1;
      min-height: 300px;
      border-radius: var(--v2-radius-lg);
    }

    .v2-pd-sk--title {
      height: 30px;
      width: 70%;
      margin-bottom: var(--v2-space-2);
    }

    .v2-pd-sk--sub {
      height: 14px;
      width: 45%;
    }

    .v2-pd-sk--price {
      height: 22px;
      width: 32%;
      margin-top: var(--v2-space-2);
    }

    .v2-pd-sk--line {
      height: 12px;
      width: 100%;
    }

    .v2-pd-sk--line-short {
      width: 60%;
    }

    .v2-pd-sk--btn {
      height: 44px;
      width: 100%;
      margin-top: var(--v2-space-4);
      border-radius: var(--v2-radius-md);
    }

    .v2-pd-sk-divider {
      height: 1px;
      background: var(--v2-border);
      margin: var(--v2-space-3) 0;
    }
  }

  &__gallery {
    display: flex;
    align-items: flex-start;
    justify-content: center;
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-2);
    min-width: 0;
  }

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--v2-space-3);
  }

  &__name {
    font-family: var(--v2-font-display);
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--v2-foreground);
    margin: 0;
    line-height: 1.3;
  }

  &__actions-wrap {
    position: relative;
  }

  &__more-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    background: none;
    border-radius: var(--v2-radius-md);
    cursor: pointer;
    color: var(--v2-muted-foreground);
    transition: background 0.15s ease;

    &:hover {
      background: var(--v2-muted);
    }
  }

  &__actions-menu {
    position: absolute;
    top: 100%;
    right: 0;
    z-index: 10;
    min-width: 180px;
    // Floating glass — uses --v2-glass-surface-floating (near-opaque white)
    // because this dropdown can render over a dark product gallery image,
    // and the regular black-tint glass would go invisible there.
    background: var(--v2-glass-surface-floating);
    backdrop-filter: blur(var(--v2-glass-blur-sm)) saturate(160%);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-lg);
    box-shadow: var(--v2-glass-shadow-floating);
    padding: var(--v2-space-2) 0;
  }

  &__actions-title {
    padding: var(--v2-space-2) var(--v2-space-4);
    font-size: 0.75rem;
    color: var(--v2-text-muted-secondary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  &__action-item {
    display: block;
    width: 100%;
    text-align: left;
    padding: var(--v2-space-2) var(--v2-space-4);
    border: none;
    background: none;
    font-size: 0.875rem;
    font-family: var(--v2-font-sans);
    color: var(--v2-foreground);
    cursor: pointer;

    &:hover {
      background: var(--v2-muted);
    }
  }

  &__breadcrumb {
    font-size: 0.8125rem;
    color: var(--v2-muted-foreground);
  }

  &__style-code {
    font-size: 0.875rem;
    color: var(--v2-text-muted-secondary);
  }

  &__segment-tags {
    margin-top: var(--v2-space-2);
  }

  &__prices {
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-1);
    margin-top: var(--v2-space-2);
  }

  &__price-item {
    font-size: 0.9375rem;
    color: var(--v2-foreground);
  }

  &__price-label {
    font-weight: 600;
    margin-right: var(--v2-space-1);
  }

  &__line-through {
    text-decoration: line-through;
    color: var(--v2-text-muted-secondary);
  }

  &__discounted {
    font-weight: 700;
    color: var(--v2-primary);
  }

  &__divider {
    height: 1px;
    background: var(--v2-border);
    margin: var(--v2-space-3) 0;
  }

  &__meta {
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-2);
  }

  &__meta-item {
    font-size: 0.875rem;
    color: var(--v2-foreground);
    line-height: 1.5;
  }

  &__meta-label {
    font-weight: 600;
    margin-right: var(--v2-space-1);
  }

  &__carton-qty {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
    margin-bottom: var(--v2-space-2);
  }

  &__qty-input {
    width: 80px;
    padding: var(--v2-space-1) var(--v2-space-2);
    border: 1px solid var(--v2-input);
    border-radius: var(--v2-radius-sm);
    font-size: 0.875rem;
    font-family: var(--v2-font-sans);
    text-align: center;

    &:focus {
      outline: none;
      border-color: var(--v2-primary);
      box-shadow: 0 0 0 2px rgba(116, 125, 238, 0.15);
    }

    &:disabled {
      background: var(--v2-muted);
      cursor: not-allowed;
    }
  }

  &__max-qty {
    font-size: 0.8125rem;
    color: var(--v2-text-muted-secondary);
  }

  &__moq {
    display: flex;
    gap: var(--v2-space-4);
    padding: var(--v2-space-3) 0;
  }

  &__moq-item {
    font-size: 0.875rem;
    color: var(--v2-primary);
    font-weight: 500;

    &--error {
      color: var(--v2-destructive);
    }
  }

  &__cta {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
    padding-top: var(--v2-space-2);

    ::v-deep .v2-wishlists-dropdown {
      flex-shrink: 0;
    }

    ::v-deep .v2-wishlists-dropdown__toggle {
      border-top: none;
      width: auto;
      padding: var(--v2-space-1) var(--v2-space-2);
      min-height: auto;
      gap: var(--v2-space-1);
      border: 1px solid var(--v2-border);
      border-radius: var(--v2-radius-md);
    }

    ::v-deep .v2-wishlists-dropdown__menu {
      min-width: 220px;
      right: 0;
      left: auto;
    }
  }

  &__recommended {
    margin-top: var(--v2-space-8);
  }

  &__section-title {
    font-family: var(--v2-font-display);
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--v2-foreground);
    margin-bottom: var(--v2-space-4);
    padding-top: var(--v2-space-4);
    border-top: 1px solid var(--v2-border);
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: var(--v2-space-4);
  }

  &__delete-modal ::v-deep .v2-modal__body {
    padding: var(--v2-space-2);
  }

  &--price-na {
    pointer-events: none;
    opacity: 0.7;
    cursor: not-allowed;
    user-select: none;
  }

  &__not-found {
    max-width: 500px;
    margin: 0 auto;
  }

  &--modal {
    padding: 0;
  }

  &--page {
    padding: 0;
  }
}

// Loading-skeleton shimmer sweep (left → right).
@keyframes v2-pd-shimmer {
  from { background-position: 200% 0; }
  to   { background-position: -200% 0; }
}
</style>
